import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from './endpoints';
import { getAuthToken, clearAuth } from '@/utils/storage';

/**
 * Axios instance with interceptors for authentication and error handling.
 * Fixes the Angular issue where AuthInterceptor wasn't properly registered.
 */

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

/**
 * Request interceptor - adds auth token to all requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();

    if (token) {
      // Using Bearer auth token
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handles errors globally
 * Enhanced error handling (fixes Angular's limited 401-only handling)
 */
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';

    // Auth endpoints handle their own errors — don't trigger global logout
    const isAuthEndpoint = requestUrl.includes('/login') ||
      requestUrl.includes('/users/signup') ||
      requestUrl.includes('/users/reset') ||
      requestUrl.includes('/users/confirmreset');

    switch (status) {
      case 401:
        if (!isAuthEndpoint) {
          // Unauthorized on a protected endpoint — clear auth and redirect
          clearAuth();
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        break;

      case 403:
        // Forbidden - user doesn't have permission
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
        break;

      case 404:
        // Not found — handled by individual API services
        break;

      case 500:
        // Server error
        console.error('Server error:', error.response?.data);
        window.dispatchEvent(new CustomEvent('server:error', {
          detail: error.response?.data
        }));
        break;

      case 0:
      case undefined:
        // Network error (no response)
        if (error.message === 'Network Error') {
          window.dispatchEvent(new CustomEvent('network:error'));
        }
        break;
    }

    // Extract error message
    const errorMessage =
      (error.response?.data as { message?: string })?.message ||
      error.message ||
      'An unexpected error occurred';

    return Promise.reject({
      status,
      message: errorMessage,
      originalError: error,
    });
  }
);

/**
 * Helper type for API error responses
 */
export interface ApiError {
  status: number | undefined;
  message: string;
  originalError: AxiosError;
}

/**
 * Helper function to check if an error is an API error
 */
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'originalError' in error
  );
};

export default api;
